import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, ArrowLeft, Clock, Image as ImageIcon, Ticket, UserPlus, Settings, Music, Sparkles, Car, Wand2 } from 'lucide-react';
import { eventsService, rsvpService } from '../services/api-service';
import { useAuth } from '../context/AuthContext';
import EventDecorationChat from '../components/EventDecorationChat';
import './EventDetails.css';
import './SecretEventPage.css';

const SecretEventPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    name: user?.name || '',
    phone: '',
    startingLocation: ''
  });
  const [rsvpLoading, setRsvpLoading] = useState(false);
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [showDecorationChat, setShowDecorationChat] = useState(false);

  useEffect(() => {
    loadEvent();
  }, [code]);

  // Apply custom styling if event has custom theme
  useEffect(() => {
    if (event && event.customCSS) {
      // Inject custom CSS
      const styleId = `event-custom-style-${event.id}`;
      let styleElement = document.getElementById(styleId);
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      styleElement.textContent = event.customCSS;
      
      return () => {
        // Cleanup on unmount
        const element = document.getElementById(styleId);
        if (element) {
          element.remove();
        }
      };
    }
  }, [event]);

  // Apply custom colors if available
  useEffect(() => {
    if (event && event.customColors) {
      const root = document.documentElement;
      if (event.customColors.primary) {
        root.style.setProperty('--event-primary-color', event.customColors.primary);
      }
      if (event.customColors.secondary) {
        root.style.setProperty('--event-secondary-color', event.customColors.secondary);
      }
      if (event.customColors.background) {
        root.style.setProperty('--event-background-color', event.customColors.background);
      }
      if (event.customColors.text) {
        root.style.setProperty('--event-text-color', event.customColors.text);
      }
    }
  }, [event]);

  const loadEvent = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventData = await eventsService.getEventByCode(code);
      if (eventData) {
        setEvent(eventData);
      } else {
        setError('Event not found. Please check your code.');
      }
    } catch (error) {
      console.error('Error loading event:', error);
      setError('Failed to load event. The code may be invalid.');
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/?login=true');
      return;
    }

    setRsvpLoading(true);
    try {
      await rsvpService.createRSVP({
        eventId: event.id,
        name: rsvpData.name,
        phone: rsvpData.phone,
        startingLocation: rsvpData.startingLocation,
        userId: user.id
      });
      setRsvpSubmitted(true);
      setTimeout(() => {
        setShowRSVPModal(false);
        setRsvpSubmitted(false);
      }, 2000);
    } catch (error) {
      console.error('Error creating RSVP:', error);
      alert('Failed to RSVP. Please try again.');
    } finally {
      setRsvpLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="event-details-page">
        <div className="container">
          <div className="event-details-loading">
            <Calendar size={64} className="spinning" />
            <h3>Loading event...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-details-page">
        <div className="container">
          <div className="event-details-error">
            <h2>Event Not Found</h2>
            <p>{error || 'The event you are looking for does not exist. Please check your code.'}</p>
            <Link to="/events" className="btn">
              <ArrowLeft size={20} />
              Back to Events
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Determine if we should apply custom theme class
  const pageClassName = event.customTheme 
    ? `event-details-page event-theme-${event.customTheme}`
    : 'event-details-page';

  return (
    <div className={pageClassName}>
      {/* Header with Back Button */}
      <section className="event-details-header">
        <div className="container">
          <Link to="/events" className="event-details-back">
            <ArrowLeft size={20} />
            Back to Events
          </Link>
        </div>
      </section>

      {/* Event Hero */}
      <section className="event-details-hero">
        <div className="container">
          {event.coverImage && (
            <div className="event-details-image">
              <img src={event.coverImage} alt={event.title} />
            </div>
          )}
          <div className="event-details-hero-content">
            <h1>{event.title}</h1>
            <div className="event-details-meta">
              <div className="event-details-meta-item">
                <Calendar size={20} />
                <span>{formatDate(event.date)}</span>
              </div>
              {event.location && !event.locationHidden && (
                <div className="event-details-meta-item">
                  <MapPin size={20} />
                  <span>{typeof event.location === 'string' ? event.location : (event.location.venue || event.location.address)}</span>
                </div>
              )}
              {event.locationHidden && (
                <div className="event-details-meta-item">
                  <MapPin size={20} />
                  <span>Location will be revealed closer to the event</span>
                </div>
              )}
              {event.time && (
                <div className="event-details-meta-item">
                  <Clock size={20} />
                  <span>{event.time.start} - {event.time.end}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Event Content */}
      <section className="event-details-content section">
        <div className="container">
          <div className="event-details-grid">
            {/* Main Content */}
            <div className="event-details-main">
              <div className="event-details-description">
                <h2>About This Event</h2>
                <p>{event.description || 'Join us for an amazing celebration!'}</p>
              </div>

              {/* What We're Celebrating */}
              {(event.celebrating || event.culturalSignificance || event.theme) && (
                <div className="secret-event-celebrating">
                  <h2>
                    <Sparkles size={24} />
                    What We're Celebrating
                  </h2>
                  {event.celebrating && (
                    <p className="celebrating-text">{event.celebrating}</p>
                  )}
                  {event.culturalSignificance && (
                    <div className="cultural-significance">
                      <h3>Cultural Significance</h3>
                      <p>{event.culturalSignificance}</p>
                    </div>
                  )}
                  {event.theme && (
                    <div className="event-theme-info">
                      <p><strong>Theme:</strong> {event.theme}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Location Section */}
              {event.location && !event.locationHidden && (
                <div className="secret-event-location">
                  <h2>
                    <MapPin size={24} />
                    Location
                  </h2>
                  <div className="location-details">
                    {typeof event.location === 'string' ? (
                      <p className="location-address">{event.location}</p>
                    ) : (
                      <>
                        {event.location.venue && (
                          <p className="location-venue"><strong>Venue:</strong> {event.location.venue}</p>
                        )}
                        {event.location.address && (
                          <p className="location-address">{event.location.address}</p>
                        )}
                        {event.location.coordinates && (
                          <div className="location-coordinates">
                            <small>Coordinates: {event.location.coordinates.lat}, {event.location.coordinates.lng}</small>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  
                  {/* Map */}
                  {(event.location.coordinates || event.location.address || typeof event.location === 'string') && (
                    <div className="event-map-container">
                      {event.location.coordinates ? (
                        <iframe
                          width="100%"
                          height="400"
                          frameBorder="0"
                          style={{ border: 0, borderRadius: '8px' }}
                          src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}&q=${event.location.coordinates.lat},${event.location.coordinates.lng}&zoom=15`}
                          allowFullScreen
                          title="Event Location Map"
                        />
                      ) : (
                        <iframe
                          width="100%"
                          height="400"
                          frameBorder="0"
                          style={{ border: 0, borderRadius: '8px' }}
                          src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}&q=${encodeURIComponent(typeof event.location === 'string' ? event.location : (event.location.address || event.location.venue))}&zoom=15`}
                          allowFullScreen
                          title="Event Location Map"
                        />
                      )}
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(typeof event.location === 'string' ? event.location : (event.location.address || event.location.venue))}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="map-link-button"
                      >
                        Open in Google Maps
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Parking Information */}
              {event.parking && (
                <div className="secret-event-parking">
                  <h2>
                    <Car size={24} />
                    Parking Information
                  </h2>
                  <div className="parking-details">
                    {event.parking.description && (
                      <p className="parking-description">{event.parking.description}</p>
                    )}
                    {event.parking.location && (
                      <div className="parking-location">
                        <p><strong>Parking Location:</strong> {event.parking.location}</p>
                      </div>
                    )}
                    {event.parking.coordinates && (
                      <div className="parking-map-container">
                        <iframe
                          width="100%"
                          height="300"
                          frameBorder="0"
                          style={{ border: 0, borderRadius: '8px', marginTop: '15px' }}
                          src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''}&q=${event.parking.coordinates.lat},${event.parking.coordinates.lng}&zoom=15`}
                          allowFullScreen
                          title="Parking Location Map"
                        />
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${event.parking.coordinates.lat},${event.parking.coordinates.lng}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="map-link-button"
                        >
                          Open Parking in Google Maps
                        </a>
                      </div>
                    )}
                    {event.parking.instructions && (
                      <div className="parking-instructions">
                        <h3>Parking Instructions</h3>
                        <ul>
                          {Array.isArray(event.parking.instructions) ? (
                            event.parking.instructions.map((instruction, index) => (
                              <li key={index}>{instruction}</li>
                            ))
                          ) : (
                            <li>{event.parking.instructions}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lineup/Artists */}
              {event.artists && event.artists.length > 0 && (
                <div className="secret-event-lineup">
                  <h2>
                    <Music size={24} />
                    Lineup
                  </h2>
                  <div className="event-details-artists-list">
                    {event.artists.map((artist, index) => (
                      <div key={artist.id || index} className="event-details-artist">
                        {artist.image && (
                          <img src={artist.image} alt={artist.name} />
                        )}
                        <div>
                          <h3>{artist.name}</h3>
                          {artist.bio && <p>{artist.bio}</p>}
                          {artist.setTime && (
                            <p className="artist-set-time">
                              <Clock size={16} />
                              {artist.setTime}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery */}
              {event.gallery && event.gallery.length > 0 && (
                <div className="event-details-gallery">
                  <h2>
                    <ImageIcon size={24} />
                    Event Gallery
                  </h2>
                  <div className="event-details-gallery-grid">
                    {event.gallery.map((photo, index) => (
                      <div key={photo.id || index} className="event-details-gallery-item">
                        <img 
                          src={photo.url || photo.thumbnailUrl || photo} 
                          alt={`${event.title} - Photo ${index + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Artists */}
              {event.artists && event.artists.length > 0 && (
                <div className="event-details-artists">
                  <h2>Artists & Performers</h2>
                  <div className="event-details-artists-list">
                    {event.artists.map((artist, index) => (
                      <div key={artist.id || index} className="event-details-artist">
                        {artist.image && (
                          <img src={artist.image} alt={artist.name} />
                        )}
                        <div>
                          <h3>{artist.name}</h3>
                          {artist.bio && <p>{artist.bio}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="event-details-sidebar">
              {/* RSVP/Ticket Section */}
              <div className="event-details-actions">
                <h3>Join This Event</h3>
                {user ? (
                  <>
                    <button 
                      className="btn btn-orange btn-block"
                      onClick={() => setShowRSVPModal(true)}
                    >
                      <UserPlus size={20} />
                      RSVP Now
                    </button>
                    <button 
                      className="btn btn-block"
                      onClick={() => navigate(`/calendar?event=${event.id}`)}
                    >
                      <Ticket size={20} />
                      Purchase Ticket
                    </button>
                  </>
                ) : (
                  <button 
                    className="btn btn-orange btn-block"
                    onClick={() => navigate('/?login=true')}
                  >
                    <UserPlus size={20} />
                    Login to RSVP
                  </button>
                )}
              </div>

              {/* Admin Actions */}
              {user && (
                <div className="event-details-admin">
                  <h3>Admin</h3>
                  <button
                    onClick={() => setShowDecorationChat(true)}
                    className="btn btn-block"
                    style={{ marginBottom: '10px' }}
                  >
                    <Wand2 size={18} />
                    Decorate with AI
                  </button>
                  <a
                    href={`/events/${event.id}/location-reveal`}
                    className="btn btn-block"
                  >
                    <Settings size={18} />
                    Location Reveal Admin
                  </a>
                </div>
              )}

              {/* Event Info */}
              <div className="event-details-info">
                <h3>Event Information</h3>
                <div className="event-details-info-item">
                  <strong>Status:</strong>
                  <span className={`event-status-badge ${event.status}`}>
                    {event.status === 'upcoming' ? 'Upcoming' : 'Completed'}
                  </span>
                </div>
                {event.attendees && (
                  <div className="event-details-info-item">
                    <Users size={18} />
                    <span>{event.attendees} attendees</span>
                  </div>
                )}
                {event.tags && event.tags.length > 0 && (
                  <div className="event-details-info-item">
                    <strong>Tags:</strong>
                    <div className="event-details-tags">
                      {event.tags.map((tag, index) => (
                        <span key={index} className="event-details-tag">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RSVP Modal */}
      {showRSVPModal && (
        <div className="rsvp-modal-overlay" onClick={() => setShowRSVPModal(false)}>
          <div className="rsvp-modal" onClick={(e) => e.stopPropagation()}>
            <h2>RSVP for {event.title}</h2>
            {rsvpSubmitted ? (
              <div className="rsvp-success">
                <UserPlus size={48} />
                <h3>RSVP Confirmed!</h3>
                <p>You're all set. Location details will be sent to you closer to the event.</p>
              </div>
            ) : (
              <form onSubmit={handleRSVP}>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-name">Name *</label>
                  <input
                    type="text"
                    id="rsvp-name"
                    value={rsvpData.name}
                    onChange={(e) => setRsvpData({ ...rsvpData, name: e.target.value })}
                    required
                    placeholder="Your name"
                  />
                </div>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-phone">Phone *</label>
                  <input
                    type="tel"
                    id="rsvp-phone"
                    value={rsvpData.phone}
                    onChange={(e) => setRsvpData({ ...rsvpData, phone: e.target.value })}
                    required
                    placeholder="Your phone number"
                  />
                </div>
                <div className="rsvp-form-group">
                  <label htmlFor="rsvp-location">Starting Location *</label>
                  <input
                    type="text"
                    id="rsvp-location"
                    value={rsvpData.startingLocation}
                    onChange={(e) => setRsvpData({ ...rsvpData, startingLocation: e.target.value })}
                    required
                    placeholder="Where will you be traveling from?"
                  />
                  <small>This helps us send location details at the right time</small>
                </div>
                <div className="rsvp-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowRSVPModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-orange" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Submitting...' : 'Confirm RSVP'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* AI Decoration Chat */}
      {showDecorationChat && user && event && (
        <EventDecorationChat
          event={event}
          onClose={() => setShowDecorationChat(false)}
          onSave={async (decoration) => {
            // Save decoration to event
            try {
              // This would call an API to save the decoration
              // For now, we'll apply it locally
              if (decoration.customCSS) {
                const styleId = `event-custom-style-${event.id}`;
                let styleElement = document.getElementById(styleId);
                if (!styleElement) {
                  styleElement = document.createElement('style');
                  styleElement.id = styleId;
                  document.head.appendChild(styleElement);
                }
                styleElement.textContent = decoration.customCSS;
              }
              alert('Decoration saved! (Note: This will be saved to the backend when the API is implemented)');
              setShowDecorationChat(false);
            } catch (error) {
              console.error('Error saving decoration:', error);
              alert('Error saving decoration. Please try again.');
            }
          }}
        />
      )}
    </div>
  );
};

export default SecretEventPage;

